import type { FormInstance } from 'antd'
import { Form } from 'antd'
import { useEffect, useRef } from 'react'

/**
 * Track whether an antd Form has been modified (dirty state).
 *
 * Compares current field values against a snapshot taken when the form
 * is first populated (via setFieldsValue or initialValues).
 *
 * @param form - antd Form instance
 * @param version - Bump this value to reset the dirty snapshot (e.g. when
 *                  editData changes or the modal re-opens with new data).
 */
export function useFormDirty(form: FormInstance, version?: string | number): boolean {
  const snapshotRef = useRef<Record<string, unknown> | null>(null)
  const initializedRef = useRef(false)
  const versionRef = useRef(version)

  // Subscribe to all field values — triggers re-render on every change.
  const allValues = (Form.useWatch([], form) as Record<string, unknown>) ?? {}

  // When version changes, reset so we re-capture the new baseline.
  useEffect(() => {
    if (version !== versionRef.current) {
      snapshotRef.current = null
      initializedRef.current = false
      versionRef.current = version
    }
  }, [version])

  // Capture the snapshot once after form values are populated.
  // This handles the case where setFieldsValue is called asynchronously
  // (e.g. in a parent useEffect) and useWatch doesn't reflect the values
  // on the first render.
  useEffect(() => {
    if (!initializedRef.current && Object.keys(allValues).length > 0) {
      snapshotRef.current = { ...allValues }
      initializedRef.current = true
    }
  }, [allValues])

  // If snapshot hasn't been captured yet, form is considered pristine.
  if (!snapshotRef.current) return false

  return !shallowEqual(allValues, snapshotRef.current)
}

/** Simple shallow equality check for objects. */
function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  for (const key of keysA) {
    if (!Object.is(a[key], b[key])) return false
  }
  return true
}
